import { useState, useEffect } from "react";
import { SkyDropdown, SkyText, SkyBadge, SkyButton, useSkyTheme } from "@skypointcloud/skypoint-ui";
import { Sun, Moon } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { IRootReducerState } from "../../../../store/IRootReducer";
import {
  instanceChange as instanceChangeAction,
  tenantChange as tenantChangeAction,
} from "../../../../store/slices/authSlice";
import { clearTenantsData } from "../../../../store/slices/tenantsSlice";
import { saveTenantsList } from "../../../../store/slices/tenantsSlice";
import { persistor } from "../../../../store";
import {
  changedInstanceLogCreate,
  getTenants,
} from "../../../../store/instanceManagement/instanceService";
import { useCurrentUser, useAccountRoleLabel } from "../hooks";
import { useGetMyPermissionsQuery } from "../../../../store/api/rbacApi";
import { authService } from "../../../../utils/authService";
import { saveUserPreferencesToDB } from "../../../../services/userPreferencesService";
import { logger } from "../../../../logger-init";

interface TenantInstanceSwitcherProps {
  onContextChange?: () => void;
  onLoadingChange?: (isLoading: boolean) => void;
}

export function TenantInstanceSwitcher({
  onContextChange,
  onLoadingChange,
}: TenantInstanceSwitcherProps) {
  const dispatch = useDispatch();

  // Get user data from hook
  const { currentUser } = useCurrentUser();

  // my-permissions is the authoritative source for role (handles skypoint_admin
  // correctly); the label comes from the same derivation the account page uses.
  const { data: rbacPerms } = useGetMyPermissionsQuery();
  const resolvedRole = useSelector((state: IRootReducerState) => state.authReducer?.resolvedRole);
  const effectiveRole = rbacPerms?.role || resolvedRole;
  const roleLabel = useAccountRoleLabel();

  const isSkyPointAdmin = effectiveRole === "skypoint_admin";

  // Get data directly from Redux (already loaded by header)
  const tenantsData = useSelector(
    (state: IRootReducerState) => state.tenantsReducer?.tenantsData || {},
  );
  const { tenants, selectedTenant } = tenantsData;
  const instancesData = useSelector(
    (state: IRootReducerState) => state.instancesReducer?.instancesData || {},
  );
  const { selectedInstanceName } = instancesData;

  // Self-sufficient: fetch data directly if Redux is empty or if skypoint_admin
  // needs the full tenant list (their B2C token may only have one tenant).
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    if (hasFetched) return;
    // Always re-fetch for skypoint_admin — cached data may be incomplete
    if (!isSkyPointAdmin && tenants && tenants.length > 0) return;

    setHasFetched(true);

    getTenants()
      .then((data) => {
        if (data?.tenants) {
          data.tenants.sort((a: { businessname: string }, b: { businessname: string }) =>
            a.businessname.toLowerCase().localeCompare(b.businessname.toLowerCase()),
          );
          dispatch(saveTenantsList(data));
        }
      })
      .catch((error) => {
        logger.error("Failed to fetch tenants", error instanceof Error ? error : undefined);
      });
  }, [tenants, hasFetched, dispatch]);

  // Derive the active tenant from the URL's instanceid rather than stale Redux
  const urlInstanceId = new URLSearchParams(window.location.search).get("instanceid") || "";
  const activeTenantFromUrl = urlInstanceId
    ? tenants?.find((t: { id: string; instances?: Array<{ id: string }> }) =>
        t.instances?.some((inst) => inst.id === urlInstanceId),
      )
    : null;
  const resolvedTenant =
    activeTenantFromUrl || tenants?.find((t: { id: string }) => t.id === selectedTenant?.id);
  const selectedTenantBusinessName = resolvedTenant?.businessname || "";

  // Context change state
  const [changedTenant, setChangedTenant] = useState<string>("");
  const [changedInstance, setChangedInstance] = useState<string>("");
  const [disabledMenu, setDisabledMenu] = useState<boolean>(false);

  const redirectToUrl = async (instanceId: string) => {
    dispatch(clearTenantsData());
    await persistor.flush();

    const url = window.location.href;
    const arr = url.split("/");
    const newUrl = `${arr[0]}//${arr[2]}?instanceid=${instanceId}`;
    window.location.href = newUrl;
  };

  const currentTenantName = changedTenant || selectedTenantBusinessName;
  const activeInstanceFromUrl = urlInstanceId
    ? (
        resolvedTenant as { instances?: Array<{ id: string; displayname?: string }> }
      )?.instances?.find((i) => i.id === urlInstanceId)
    : null;
  const currentInstanceName =
    changedInstance || activeInstanceFromUrl?.displayname || selectedInstanceName;

  const currentRole = roleLabel;

  // When changing tenant, derive instances from the tenants array to stay in sync
  const currentTenantForInstances = changedTenant
    ? tenants?.find((t: { id: string; businessname: string }) => t.businessname === changedTenant)
    : null;

  const handleTenantSelect = async (tenantId: string, tenantName: string) => {
    if (tenantName === currentTenantName) {
      return;
    }

    onLoadingChange?.(true);
    setDisabledMenu(true);
    setChangedTenant(tenantName);
    onContextChange?.();

    await changedInstanceLogCreate("Tenant");
    dispatch(tenantChangeAction());

    const userTenants = currentUser?.tenants as
      | Array<{ tenantid: string; instances?: Array<{ instanceid: string }> }>
      | undefined;
    const userTenantAccess =
      !currentUser?.isPlatformAdmin && userTenants
        ? userTenants.find((x) => x.tenantid === tenantId)
        : null;

    const instanceId =
      userTenantAccess?.instances?.[0]?.instanceid ||
      tenants?.find((x: { id: string; instances: Array<{ id: string }> }) => x.id === tenantId)
        ?.instances[0]?.id;

    if (instanceId) {
      authService.saveTenantId(tenantId);
      authService.saveInstanceId(instanceId);
      redirectToUrl(instanceId);
    }
  };

  const handleInstanceSelect = async (instanceId: string, instanceName: string) => {
    if (instanceName === currentInstanceName) {
      return;
    }

    onLoadingChange?.(true);
    setDisabledMenu(true);
    setChangedInstance(instanceName);
    onContextChange?.();

    await changedInstanceLogCreate("Instance");
    dispatch(instanceChangeAction());

    authService.saveInstanceId(instanceId);
    redirectToUrl(instanceId);
  };

  // Prepare dropdown options
  const tenantOptions =
    tenants?.map((tenant: { id: string; businessname: string }) => ({
      value: tenant.id,
      text: tenant.businessname,
    })) || [];

  const selectedTenantInstances = resolvedTenant?.instances || [];
  const instancesToUse = currentTenantForInstances?.instances || selectedTenantInstances || [];

  const sortedInstances = [...instancesToUse].sort(
    (a: { displayname?: string; id: string }, b: { displayname?: string; id: string }) =>
      (a.displayname || a.id).toLowerCase().localeCompare((b.displayname || b.id).toLowerCase()),
  );

  const instanceOptions = sortedInstances.map((instance: { id: string; displayname?: string }) => ({
    value: instance.id,
    text: instance.displayname || instance.id,
  }));

  const selectedTenantKey = tenants?.find(
    (t: { id: string; businessname: string }) => t.businessname === currentTenantName,
  )?.id;
  const selectedInstanceKey = instancesToUse?.find(
    (i: { id: string; displayname?: string }) => i.displayname === currentInstanceName,
  )?.id;

  const hasMultipleTenants = (tenants?.length ?? 0) > 1;
  const hasMultipleInstances = instancesToUse.length > 1;

  // Skypoint Admin always sees dropdowns — they have cross-tenant access
  const showTenantDropdown = hasMultipleTenants || isSkyPointAdmin;
  const showInstanceDropdown = hasMultipleInstances || isSkyPointAdmin;

  // Hide only when there's nothing to show (no tenants, no instances, no role)
  const hasSingleContext = !hasMultipleTenants && !hasMultipleInstances;

  if (hasSingleContext && !currentInstanceName && !currentRole) {
    return null;
  }

  return (
    <div className="px-4 py-3 border-b border-gray-200 flex flex-col gap-2.5">
      {showTenantDropdown ? (
        <SkyDropdown
          label="Tenant"
          size="small"
          options={tenantOptions}
          selectedOptions={selectedTenantKey ? [selectedTenantKey] : []}
          onOptionSelect={(values) => {
            const tenantId = values[0];
            const tenant = tenants?.find(
              (t: { id: string; businessname: string }) => t.id === tenantId,
            );
            if (tenant) {
              handleTenantSelect(tenantId, tenant.businessname);
            }
          }}
          disabled={disabledMenu}
          listboxMaxHeight="250px"
        />
      ) : null}
      {showInstanceDropdown ? (
        <SkyDropdown
          label="Instance"
          size="small"
          options={instanceOptions}
          selectedOptions={selectedInstanceKey ? [selectedInstanceKey] : []}
          onOptionSelect={(values) => {
            const instanceId = values[0];
            const instance = instancesToUse?.find(
              (i: { id: string; displayname?: string }) => i.id === instanceId,
            );
            if (instance) {
              handleInstanceSelect(instanceId, instance.displayname || instance.id);
            }
          }}
          disabled={disabledMenu}
          listboxMaxHeight="250px"
        />
      ) : currentInstanceName ? (
        <div className="flex items-center gap-2">
          <SkyText
            size={200}
            className="text-gray-500"
          >
            Instance
          </SkyText>
          <SkyText
            size={200}
            weight="semibold"
          >
            {currentInstanceName}
          </SkyText>
        </div>
      ) : null}
      {currentRole && (
        <div
          className={`flex items-center gap-2${showTenantDropdown || showInstanceDropdown || currentInstanceName ? " border-t border-gray-200 pt-3 mt-1" : ""}`}
        >
          <SkyText
            size={200}
            className="text-gray-500"
          >
            Role
          </SkyText>
          <SkyBadge
            appearance="filled"
            color="warning"
            className="bg-[#FEF3C7]! text-[#92400E]! text-xs"
          >
            {currentRole}
          </SkyBadge>
          <ThemeToggle />
        </div>
      )}
    </div>
  );
}

function ThemeToggle() {
  const { isDarkMode, setMode } = useSkyTheme();

  const toggle = () => {
    const next = isDarkMode ? "light" : "dark";
    setMode(next);
    saveUserPreferencesToDB({ theme: next === "dark" ? "Dark" : "Light" });
  };

  return (
    <SkyButton
      appearance="subtle"
      size="small"
      icon={isDarkMode ? Sun : Moon}
      onClick={toggle}
      aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
      className="ml-auto"
    />
  );
}
